import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, User, Briefcase, Warning, FileText, X, Receipt, CurrencyInr, IdentificationBadge, HouseSimple, UsersThree, Plus, UserPlus, FilePlus, Megaphone, Wallet, Rocket } from "@phosphor-icons/react";

// Quick-create shortcuts triggered when the user types "+something"
const QUICK_ACTIONS = [
  { key: "customer",    label: "New Customer",     hint: "Add a new customer record",         icon: UserPlus,          to: "/customers?create=1" },
  { key: "application", label: "New Application",  hint: "Register a service application",    icon: FilePlus,          to: "/applications?create=1" },
  { key: "lead",        label: "New Lead",         hint: "Log a marketing lead",              icon: Megaphone,         to: "/marketing?create=1" },
  { key: "bill",        label: "New POS Bill",     hint: "Create a POS invoice",              icon: Receipt,           to: "/pos?create=1" },
  { key: "expense",     label: "New Expense",      hint: "Record a business expense",         icon: Wallet,            to: "/expenses?create=1" },
  { key: "complaint",   label: "New Complaint",    hint: "File a customer complaint",         icon: Warning,           to: "/complaints?create=1" },
  { key: "rent",        label: "New Rent Agreement", hint: "Draft an e-stamped rent agreement", icon: HouseSimple,     to: "/rent-agreements?create=1" },
  { key: "employee",    label: "New Employee",     hint: "Onboard a new staff member",        icon: UsersThree,        to: "/hr?create=1" },
  { key: "track",       label: "Track Application", hint: "Open the tracking dashboard",      icon: Rocket,            to: "/tracking" },
];

export default function GlobalSearch({ autoFocus = false, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isQuick = q.trim().startsWith("+");
  const quickTerm = isQuick ? q.trim().slice(1).toLowerCase() : "";
  const quickMatches = isQuick
    ? (quickTerm ? QUICK_ACTIONS.filter((a) => a.key.startsWith(quickTerm) || a.label.toLowerCase().includes(quickTerm)) : QUICK_ACTIONS)
    : [];

  useEffect(() => {
    if (!q || q.length < 2 || isQuick) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch (_) { setResults(null); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, isQuick]);

  const go = (to) => { setOpen(false); setQ(""); onClose?.(); nav(to); };
  const total = results ? (
    results.customers.length + results.applications.length + results.complaints.length +
    results.documents.length + (results.pos_bills?.length || 0) + (results.payments?.length || 0) +
    (results.pvc_prints?.length || 0) + (results.employees?.length || 0) + (results.rent_agreements?.length || 0)
  ) : 0;

  return (
    <div className="relative w-full max-w-2xl" ref={boxRef} data-testid="global-search-container">
      <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isQuick && quickMatches.length > 0) {
            e.preventDefault();
            go(quickMatches[0].to);
          }
        }}
        placeholder="Search anything… or type + for quick create (+customer, +application) · Ctrl+K"
        data-testid="global-search-input"
        className="pl-11 pr-16 h-12 rounded-full bg-white border-slate-300 shadow-sm focus-visible:ring-orange-600/30 focus-visible:border-orange-600 text-sm"
      />
      <kbd className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500 hidden md:block">⌘/Ctrl K</kbd>
      {q && (
        <button
          onClick={() => { setQ(""); setResults(null); inputRef.current?.focus(); }}
          className="absolute right-14 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"
          data-testid="global-search-clear"
        >
          <X size={16} />
        </button>
      )}

      {open && isQuick && (
        <div className="absolute z-40 left-0 right-0 mt-2 bg-white border border-orange-200 rounded-lg shadow-lg overflow-hidden" data-testid="global-search-quick">
          <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 flex items-center gap-2">
            <Plus size={14} className="text-orange-600" weight="bold" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-orange-700">Quick Create</span>
            {quickTerm && <span className="text-[10px] text-slate-500 font-mono">+{quickTerm}</span>}
            <span className="ml-auto text-[10px] text-slate-400">Enter to open first</span>
          </div>
          {quickMatches.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">No quick-actions match "<span className="font-mono">+{quickTerm}</span>"</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-50">
              {quickMatches.map((a) => {
                const Ic = a.icon;
                return (
                  <button
                    key={a.key}
                    onClick={() => go(a.to)}
                    data-testid={`gs-quick-${a.key}`}
                    className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                      <Ic size={16} className="text-orange-600" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{a.label}</div>
                      <div className="text-[11px] text-slate-500">{a.hint}</div>
                    </div>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">+{a.key}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {open && !isQuick && q.length >= 2 && (
        <div className="absolute z-40 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-testid="global-search-results">
          {loading && <div className="px-4 py-6 text-sm text-slate-500 text-center">Searching…</div>}
          {!loading && results && total === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500 text-center">No matches for "<span className="font-mono">{q}</span>"</div>
          )}
          {!loading && results && total > 0 && (
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100">
              <Section title="Customers" count={results.customers.length} icon={User} accent="orange">
                {results.customers.map((c) => (
                  <ResultRow key={c.id} testid={`gs-cust-${c.id}`} onClick={() => go(`/customers?id=${c.id}`)}
                    left={c.name} right={<span className="font-mono text-xs text-slate-500">{c.customer_no}</span>}
                    sub={`${c.mobile} · ${[c.city, c.state].filter(Boolean).join(", ") || "—"}`} />
                ))}
              </Section>
              <Section title="Applications" count={results.applications.length} icon={Briefcase} accent="blue">
                {results.applications.map((a) => (
                  <ResultRow key={a.id} testid={`gs-app-${a.id}`} onClick={() => go(`/applications?id=${a.id}`)}
                    left={<span>{a.service_name} · <span className="text-slate-500">{a.customer_name}</span></span>}
                    right={<span className="font-mono text-xs text-slate-500">{a.app_no}</span>}
                    sub={`${a.status} · ${a.payment_status}${a.portal_app_no ? ` · Portal #${a.portal_app_no}` : ""}`} />
                ))}
              </Section>
              <Section title="POS Bills" count={results.pos_bills?.length || 0} icon={Receipt} accent="purple">
                {(results.pos_bills || []).map((b) => (
                  <ResultRow key={b.id} testid={`gs-pos-${b.id}`} onClick={() => go(`/pos`)}
                    left={<span>{b.customer_name} · <span className="text-slate-500">{b.customer_mobile || "—"}</span></span>}
                    right={<span className="font-mono text-xs text-slate-500">{b.pos_no}</span>}
                    sub={`₹${Number(b.total).toLocaleString("en-IN")} · ${b.payment_status}`} />
                ))}
              </Section>
              <Section title="Payments" count={results.payments?.length || 0} icon={CurrencyInr} accent="emerald">
                {(results.payments || []).map((p) => (
                  <ResultRow key={p.id} testid={`gs-pay-${p.id}`} onClick={() => go(`/payment-center`)}
                    left={<span>{p.customer_name} · <span className="text-slate-500">₹{Number(p.amount).toLocaleString("en-IN")}</span></span>}
                    right={<span className="font-mono text-[10px] text-slate-500">{p.transaction_id}</span>}
                    sub={`${p.method} · ${p.provider} · ${p.status}`} />
                ))}
              </Section>
              <Section title="PVC Prints" count={results.pvc_prints?.length || 0} icon={IdentificationBadge} accent="indigo">
                {(results.pvc_prints || []).map((v) => (
                  <ResultRow key={v.id} testid={`gs-pvc-${v.id}`} onClick={() => go(`/print-center?t=history`)}
                    left={<span>{v.template_name} · <span className="text-slate-500">{v.customer_name}</span></span>}
                    right={<span className="text-[10px] text-slate-500">{v.paper}</span>}
                    sub={v.printed_at ? new Date(v.printed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""} />
                ))}
              </Section>
              <Section title="Rent Agreements" count={results.rent_agreements?.length || 0} icon={HouseSimple} accent="blue">
                {(results.rent_agreements || []).map((r) => (
                  <ResultRow key={r.id} testid={`gs-ra-${r.id}`} onClick={() => go(`/rent-agreements`)}
                    left={<span>{r.customer_name || r.tenant_name} · <span className="text-slate-500">₹{Number(r.monthly_rent || 0).toLocaleString("en-IN")}</span></span>}
                    right={<span className="font-mono text-xs text-slate-500">{r.agreement_no}</span>}
                    sub={`${r.status || ""}`} />
                ))}
              </Section>
              <Section title="Employees" count={results.employees?.length || 0} icon={UsersThree} accent="orange">
                {(results.employees || []).map((e) => (
                  <ResultRow key={e.id} testid={`gs-emp-${e.id}`} onClick={() => go(`/hr`)}
                    left={<span>{e.name} · <span className="text-slate-500">{e.designation || "—"}</span></span>}
                    right={<span className="font-mono text-xs text-slate-500">{e.emp_no}</span>} sub={e.mobile} />
                ))}
              </Section>
              <Section title="Complaints" count={results.complaints.length} icon={Warning} accent="red">
                {results.complaints.map((c) => (
                  <ResultRow key={c.id} testid={`gs-cmp-${c.id}`} onClick={() => go(`/complaints`)}
                    left={<span>{c.category} · <span className="text-slate-500">{c.customer_name}</span></span>}
                    right={<span className="font-mono text-xs text-slate-500">{c.complaint_no}</span>}
                    sub={`${c.status} · ${c.priority}`} />
                ))}
              </Section>
              <Section title="Documents" count={results.documents.length} icon={FileText} accent="emerald">
                {results.documents.map((d) => (
                  <ResultRow key={d.id} testid={`gs-doc-${d.id}`} onClick={() => go(`/customers?id=${d.customer_id}&tab=documents`)}
                    left={<span>{d.doc_type} · <span className="text-slate-500">{d.customer_name}</span></span>}
                    right={<span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{d.category}</span>}
                    sub={d.file_name} />
                ))}
              </Section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, count, icon: Icon, accent, children }) {
  if (!count) return null;
  const tone = { orange: "text-orange-600", blue: "text-blue-700", red: "text-red-600", emerald: "text-emerald-700", purple: "text-purple-700", indigo: "text-indigo-700" }[accent] || "text-slate-600";
  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200">
        <Icon size={14} weight="duotone" className={tone} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</span>
        <span className="text-[10px] text-slate-400">({count})</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ResultRow({ left, right, sub, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors block border-b border-slate-50 last:border-0">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-slate-900 font-medium truncate">{left}</div>
        <div>{right}</div>
      </div>
      {sub && <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>}
    </button>
  );
}
